import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { staffingDatePathForRoute } from "../../../utils/staffingRoutes";
import { usePerformGlobalRefresh } from "../../header/hooks";
import { PullToRefresh } from "../../UIToolkit/PullToRefresh/PullToRefresh";
import { useLoadedStaffingDays } from "../hooks/useLoadedStaffingDays";
import { MobileStaffingContent } from "./MobileStaffingContent";
import { MobileStaffingHeader } from "./MobileStaffingHeader";
import { StaffingActionFab } from "./StaffingActionFab";

export const MobileStaffing = () => {
	const { dateString } = useParams();
	const { hospitalData, chosenDepartment } = useHospitalData();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [selectedDate, setSelectedDate] = useState<DateTime>(dateString ? DateTime.fromISO(dateString) : DateTime.now());

	const weekIndex: number = useMemo(() => {
		const formattedDate: string = selectedDate.toFormat("yyyy-MM-dd");
		// `weeklyShifts[0]` and its `days` are briefly absent while a week hydrates during a refresh, so
		// step over those weeks instead of dereferencing them.
		return hospitalData.weeks.findIndex((week) => week.weeklyShifts[0]?.days?.some((day) => day.date.toFormat("yyyy-MM-dd") === formattedDate));
	}, [selectedDate, hospitalData.weeks]);

	const loadedDays = useLoadedStaffingDays();

	const scrollRef = useRef<HTMLDivElement | null>(null);
	const childrenContainerRef = useRef<HTMLDivElement | null>(null);
	const [staffingHeaderHeight, setStaffingHeaderHeight] = useState<number>(0);
	const { performGlobalRefresh } = usePerformGlobalRefresh({});

	const handleDateChange = useCallback(
		(newDate: DateTime) => {
			setSelectedDate(newDate);
			// Stay on the route we are ON. `staffing/classic` and `staffing2` are pinned to this screen in
			// the route table, while the bare `staffing/:dateString?` is the URL the gaps-first switch owns
			// — so hardcoding the bare shape here let a classic screen navigate itself into the gaps shell
			// on a day change, and via the out-of-range redirect below, without the user touching anything.
			navigate(staffingDatePathForRoute(pathname, chosenDepartment.department_id, newDate.toFormat("yyyy-MM-dd")));
		},
		[navigate, pathname, chosenDepartment.department_id],
	);

	useEffect(() => {
		// `selectedDate` comes straight from the URL and is never checked against the schedule we actually
		// loaded, so a shared/stale link, a notification deep link, or a malformed date leaves `weekIndex`
		// at -1. Every descendant indexes `weeks[weekIndex]`, so redirect onto the nearest loaded day
		// instead of rendering a week that does not exist. Clamping to a real `day.date` guarantees the
		// next `findIndex` matches, so this cannot re-fire.
		if (weekIndex !== -1 || !loadedDays.length) return;

		const firstLoadedDay = loadedDays[0];
		const lastLoadedDay = loadedDays[loadedDays.length - 1];
		// DEFAULT: an out-of-range or unparseable URL date falls back to the nearest range boundary, and an
		// unparseable one is treated like a date that precedes the range (an invalid DateTime compares false
		// against everything, so it cannot be placed). This does not mask missing data — the schedule loaded
		// fine, only the requested date is not in it, and the snackbar in MobileStaffingHeader says so. The
		// alternative is rendering `weeks[-1]`, which is the crash this replaces.
		handleDateChange(!selectedDate.isValid || selectedDate < firstLoadedDay ? firstLoadedDay : lastLoadedDay);
	}, [weekIndex, loadedDays, selectedDate, handleDateChange]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				width: "100%",
				py: 2,
			}}
		>
			<MobileStaffingHeader
				weekIndex={weekIndex}
				date={selectedDate}
				setDate={handleDateChange}
				onRootHeightChange={(height) => setStaffingHeaderHeight(height)}
			/>
			<Box
				id={"scrollable-staffing-container"}
				ref={scrollRef}
				style={{
					overflowY: "auto",
					overscrollBehaviorY: "contain",
					WebkitOverflowScrolling: "touch",
					background: "#f8f8f8",
				}}
				sx={{
					height: `calc(100% - ${staffingHeaderHeight}px)`,
					position: "relative",
					width: "100%",
				}}
			>
				<PullToRefresh
					key={"mobile-staffing-pull-to-refresh"}
					childrenContainerEl={childrenContainerRef.current}
					scrollerEl={scrollRef.current}
					onRefresh={performGlobalRefresh}
				/>
				<Box ref={childrenContainerRef} id={"mobile-staffing-content-box"} sx={{ pt: 2 }}>
					<MobileStaffingContent weekIndex={weekIndex} date={selectedDate} />
				</Box>
			</Box>
			<StaffingActionFab date={selectedDate.toFormat("yyyy-MM-dd")} />
		</Box>
	);
};
